import { QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { CLIENT_TOOL_GROUPS, CLIENT_TOOLS } from '@cc/schema';
import { useCustom, useGetIdentity, useGetLocale, useTranslate } from '@refinedev/core';
import { Button, Card, ConfigProvider, Empty, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { createStyles } from 'antd-style';
import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import React, { useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ClientName, ShowNamesButton, useClientNamesVisibility } from '../../components/clientNames';
import { GROUP_LABEL_KEYS, toolLabel } from '../../components/clientTools/labels';
import { useClientRoster } from '../../hooks/client-options';
import { IUser } from '../../providers';
import { CaseloadTabs } from './CaseloadTabs';
import {
  SIGNALS,
  isFiltered,
  paramsOfView,
  signalCounts,
  viewRows,
  viewStateOf,
  type CaseloadRow,
  type CaseloadViewState,
  type Signal,
  type SortKey,
  type SortOrder,
} from './caseloadView';
import { formatDay } from './formatDay';

/**
 * E57 · CaseloadOverview — Caseload › Overview (cc-practice, screen "Caseload — Overview", e57e0002-…-015): the therapist's
 * clients side by side, one row each.
 *
 * D-nav-fixes-2026-10-19 (founder Q-nav-1 A, Q-nav-8 A): a filter bar — search by name or ID; the four questions (Homework
 * behind · Score went up · No check-in for 7+ days · Diary not filled this week) as MULTI-select chips with their counts,
 * combined as "any of"; a "tool in use" filter grouped like My tools — every column sortable, and the WHOLE ROW opens the
 * client: a click opens it, Ctrl/⌘-click and a middle click open a new tab, Enter opens it from the keyboard, and the name
 * is a real link (right-click → open in a new tab). New columns: Last session, Last activity, Tools in use. Filters and
 * sort live in the URL. The counts live HERE only — nothing is counted onto the menu, nothing notifies anyone (Q-pia-4 A).
 *
 * ACCESS is the API's (FQ-1 B, P020): a therapist's own clients; an admin's own plus the clients who allowed admin view; a
 * client who does not share view is a row with no clinical content ("does not share their records with you").
 *
 * Behavior lines implemented here:
 *   · gesture: a question chip toggles a filter; several combine as "any of"; each shows how many clients answer it
 *     (caseload.chip.*);
 *   · gesture: search by name or ID; the tool filter (caseload.search, caseload.toolFilter);
 *   · gesture: a click on a column title sorts by it (both ways);
 *   · nav: the row opens the client page (new tab on Ctrl/⌘/middle click) (caseload.rowHint);
 *   · empty-state: no clients → what will appear once clients join (caseload.empty); none matches → caseload.noMatch;
 *   · nav: Homework and All records tabs (caseload.tab.*);
 *   · format: "this week" / "today" are the last 7 days in the therapist's browser zone, passed to the query
 *     (caseload.week);
 *   · format: client names follow Show names / Hide names (60 s) (clientNames.show).
 */
export const CASELOAD_FIELDS = [
  'subject',
  'view',
  'therapist',
  'therapistName',
  'homeworkBehind',
  { latestScores: ['instrument', 'score', 'previous', 'change', 'wentUp', 'at'] },
  'scoreWentUp',
  'maxScoreRise',
  'checkInsInUse',
  'lastCheckInDay',
  'daysSinceCheckIn',
  'diaryInUse',
  'diaryDaysThisWeek',
  { lastSession: ['start', 'end'] },
  'lastActivityAt',
  'toolsInUse',
  { signals: [...SIGNALS] },
];

const browserZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const useStyles = createStyles(({ token, css }) => ({
  bar: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 4px 0 12px;
  `,
  chip: css`
    &.ant-btn {
      font-weight: 500;
    }
  `,
  chipCount: css`
    font-size: 12px;
    font-weight: 600;
    opacity: 0.75;
  `,
  summary: css`
    margin-left: auto;
    color: ${token.colorTextTertiary};
    font-size: 13.5px;
    white-space: nowrap;
  `,
  // the prototype's density (D-nav-fixes-2026-10-19): 14 px text, 8 px rows — more clients on one screen
  table: css`
    .ant-table-thead > tr > th {
      white-space: nowrap;
      padding: 10px 12px;
      font-size: 14px;
    }
    .ant-table-tbody > tr > td {
      padding: 8px 12px;
      font-size: 14px;
      line-height: 1.45;
    }
    .ant-table-tbody > tr > td:first-child {
      white-space: nowrap;
    }
    .cc-caseload-row {
      cursor: pointer;
    }
    .cc-caseload-row:focus-visible {
      outline: none;
    }
    .cc-caseload-row:focus-visible > td {
      background: #eef4ee !important;
      box-shadow: inset 0 1px 0 ${token.colorPrimaryBorder}, inset 0 -1px 0 ${token.colorPrimaryBorder};
    }
    .cc-caseload-row:hover .cc-caseload-name {
      color: ${token.colorPrimary};
      text-decoration: underline;
    }
    .cc-caseload-name {
      color: ${token.colorText};
      font-weight: 600;
    }
  `,
}));

const clientHref = (subject: string) => `/clients/show/${encodeURIComponent(subject)}`;
const keyOf = (subject: string) => subject.replace('users/', '');

export const CaseloadOverviewPage: React.FC = () => {
  const t = useTranslate();
  const getLocale = useGetLocale();
  const dateLocale = getLocale()?.startsWith('ru') ? ru : enUS;
  const navigate = useNavigate();
  const { styles } = useStyles();
  const [params, setParams] = useSearchParams();
  const view = useMemo(() => viewStateOf(params), [params]);
  const setView = useCallback(
    (next: Partial<CaseloadViewState>, replace = false) => setParams(paramsOfView({ ...view, ...next }), { replace }),
    [view, setParams],
  );
  const names = useClientNamesVisibility();
  const { clients } = useClientRoster();
  const { data: identity } = useGetIdentity<IUser>();
  const me = identity?.db?._id;

  const { data, isLoading, isError } = useCustom<CaseloadRow[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'caseloadOverview',
      fields: CASELOAD_FIELDS,
      variables: {
        // one fetch; the chips filter on the rows' signals (the server's one definition of each question)
        question: { name: 'question', type: 'String', value: 'all' },
        timeZone: { name: 'timeZone', type: 'String', value: browserZone() },
      },
    },
  });
  const all = useMemo(() => data?.data ?? [], [data]);
  const nameOf = useCallback((subject: string) => clients.find((c) => c._id === subject), [clients]);
  const rows = useMemo(() => viewRows(all, view, nameOf), [all, view, nameOf]);
  const counts = useMemo(() => signalCounts(all), [all]);
  const showTherapist = all.some((r) => r.therapist && r.therapist !== me);

  const daysAgoText = useCallback(
    (d: number) => (d <= 0 ? t('caseload.today') : d === 1 ? t('caseload.yesterday') : t('caseload.daysAgo', { count: d })),
    [t],
  );

  const sortOrderOf = (key: SortKey): SortOrder | null => (view.sort === key ? view.order : null);
  const sortable = (key: SortKey) => ({ key, sorter: true, sortOrder: sortOrderOf(key), sortDirections: ['ascend', 'descend'] as SortOrder[] });

  const columns = useMemo(
    () => [
      {
        ...sortable('client'),
        title: t('caseload.col.client'),
        render: (_v: unknown, r: CaseloadRow) => {
          const c = nameOf(r.subject);
          // a REAL link: right-click → open in a new tab, Ctrl/⌘-click, middle click — the browser's own
          return (
            <Link
              to={clientHref(r.subject)}
              className="cc-caseload-name"
              data-testid={`cc-caseload-open-${keyOf(r.subject)}`}
              onClick={(e) => e.stopPropagation()}
            >
              <ClientName id={r.subject} firstName={c?.firstName} lastName={c?.lastName} visible={names.visible} />
            </Link>
          );
        },
      },
      ...(showTherapist
        ? [
            {
              ...sortable('therapist'),
              title: t('caseload.col.therapist'),
              render: (_v: unknown, r: CaseloadRow) => r.therapistName ?? '—',
            },
          ]
        : []),
      {
        ...sortable('lastSession'),
        title: t('caseload.col.lastSession'),
        render: (_v: unknown, r: CaseloadRow) => (
          <span style={{ whiteSpace: 'nowrap' }} data-testid={`cc-caseload-last-session-${keyOf(r.subject)}`}>
            {formatDay(r.lastSession?.start, dateLocale === ru ? 'EEEEEE d MMM' : 'EEE d MMM', dateLocale)}
          </span>
        ),
      },
      {
        ...sortable('lastActivity'),
        title: t('caseload.col.lastActivity'),
        render: (_v: unknown, r: CaseloadRow) => {
          const at = r.view && r.lastActivityAt ? parseISO(r.lastActivityAt) : null;
          return (
            <span style={{ whiteSpace: 'nowrap' }} data-testid={`cc-caseload-last-activity-${keyOf(r.subject)}`}>
              {at && isValid(at) ? daysAgoText(differenceInCalendarDays(new Date(), at)) : '—'}
            </span>
          );
        },
      },
      {
        ...sortable('homework'),
        title: t('caseload.col.homework'),
        render: (_v: unknown, r: CaseloadRow) =>
          !r.view ? (
            <Typography.Text type="secondary" data-testid={`cc-caseload-not-shared-${keyOf(r.subject)}`}>
              {t('caseload.notShared')}
            </Typography.Text>
          ) : r.homeworkBehind ? (
            <Tag color="orange" data-testid={`cc-caseload-behind-${keyOf(r.subject)}`}>
              {t('caseload.behind', { count: r.homeworkBehind })}
            </Tag>
          ) : (
            '—'
          ),
      },
      {
        ...sortable('score'),
        title: t('caseload.col.latestScore'),
        render: (_v: unknown, r: CaseloadRow) =>
          r.view && r.latestScores?.length ? (
            <Space direction="vertical" size={0}>
              {r.latestScores.map((s) => (
                <span
                  key={s.instrument}
                  style={{ whiteSpace: 'nowrap' }}
                  data-testid={`cc-caseload-score-${keyOf(r.subject)}-${s.instrument}`}
                >
                  {s.instrument} {s.score}
                  {s.previous !== null && s.previous !== undefined ? (
                    <Typography.Text type={s.wentUp ? 'warning' : 'secondary'} style={{ marginLeft: 6 }}>
                      {t('caseload.scoreWas', { previous: s.previous })}
                    </Typography.Text>
                  ) : null}
                </span>
              ))}
            </Space>
          ) : (
            '—'
          ),
      },
      {
        ...sortable('checkin'),
        title: t('caseload.col.lastCheckIn'),
        render: (_v: unknown, r: CaseloadRow) => {
          if (!r.view || !r.checkInsInUse) return '—';
          const d = r.daysSinceCheckIn;
          return (
            <span style={{ whiteSpace: 'nowrap' }}>
              {d === null || d === undefined ? t('caseload.never') : daysAgoText(d)}
            </span>
          );
        },
      },
      {
        ...sortable('diary'),
        title: t('caseload.col.diary'),
        render: (_v: unknown, r: CaseloadRow) =>
          r.view && r.diaryInUse ? t('caseload.diaryDays', { count: r.diaryDaysThisWeek ?? 0 }) : '—',
      },
      {
        ...sortable('tools'),
        title: t('caseload.col.toolsInUse'),
        render: (_v: unknown, r: CaseloadRow) =>
          r.view && r.toolsInUse ? (
            <Tooltip title={r.toolsInUse.length ? r.toolsInUse.map((k) => toolLabel(t, k)).join(', ') : undefined}>
              <span data-testid={`cc-caseload-tools-${keyOf(r.subject)}`}>{r.toolsInUse.length}</span>
            </Tooltip>
          ) : (
            '—'
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, names.visible, nameOf, showTherapist, view.sort, view.order, dateLocale, daysAgoText],
  );

  // the tool filter: grouped like My tools (Plan · Track · Formulation · Skills & exercises · Safety)
  const toolOptions = CLIENT_TOOL_GROUPS.map((g) => ({
    label: t(GROUP_LABEL_KEYS[g]),
    options: CLIENT_TOOLS.filter((d) => d.group === g).map((d) => ({ value: d.key, label: toolLabel(t, d.key) })),
  }));

  const toggleSignal = (s: Signal) =>
    setView({ signals: view.signals.includes(s) ? view.signals.filter((x) => x !== s) : [...view.signals, s] });

  /** The row as the link (Q-nav-1 A): click opens; Ctrl/⌘-click and a middle click open a new tab; Enter from the keyboard. */
  const openRow = (subject: string, newTab: boolean) => {
    if (newTab) window.open(clientHref(subject), '_blank', 'noopener');
    else navigate(clientHref(subject));
  };
  const interactive = (target: EventTarget | null) =>
    target instanceof Element && !!target.closest('a,button,input,.ant-select,.ant-tooltip');

  const filtered = isFiltered(view);

  return (
    <Card>
      <CaseloadTabs active="overview" />
      <ConfigProvider
        theme={{
          components: {
            Button: { controlHeight: 32, paddingInline: 12, contentFontSize: 14 },
            Input: { controlHeight: 36 },
            Select: { controlHeight: 36 },
          },
        }}
      >
        <div className={styles.bar} role="search" aria-label={t('caseload.filtersAria')} data-testid="cc-caseload-filters">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t('caseload.search')}
            aria-label={t('caseload.search')}
            value={view.q}
            onChange={(e) => setView({ q: e.target.value }, true)}
            style={{ width: 240 }}
            data-testid="cc-caseload-search"
          />
          <span data-testid="cc-caseload-chips" style={{ display: 'contents' }}>
            {SIGNALS.map((s) => {
              const on = view.signals.includes(s);
              return (
                <Tooltip key={s} title={t(`caseload.chipHint.${s}`)}>
                  <Button
                    className={styles.chip}
                    type={on ? 'primary' : 'default'}
                    onClick={() => toggleSignal(s)}
                    aria-pressed={on}
                    data-testid={`cc-caseload-chip-${s}`}
                  >
                    {t(`caseload.chip.${s}`)}
                    <span className={styles.chipCount} data-testid={`cc-caseload-chip-${s}-count`}>
                      {isLoading ? '' : counts[s]}
                    </span>
                  </Button>
                </Tooltip>
              );
            })}
          </span>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('caseload.toolFilter')}
            aria-label={t('caseload.toolFilter')}
            value={view.tool ?? undefined}
            onChange={(tool?: string) => setView({ tool: tool ?? null })}
            options={toolOptions}
            style={{ width: 220 }}
            popupMatchSelectWidth={260}
            data-testid="cc-caseload-tool-filter"
          />
          <ShowNamesButton visible={names.visible} onToggle={names.toggle} testId="cc-caseload-toggle-names" />
          <Tooltip title={t('caseload.intro')}>
            <QuestionCircleOutlined aria-label={t('caseload.intro')} tabIndex={0} data-testid="cc-caseload-intro" />
          </Tooltip>
          <span className={styles.summary} data-testid="cc-caseload-count">
            {t('caseload.count', { shown: rows.length, total: all.length })}
            {filtered ? (
              <>
                {' · '}
                <Typography.Link
                  onClick={() => setView({ q: '', signals: [], tool: null })}
                  data-testid="cc-caseload-clear"
                >
                  {t('caseload.clear')}
                </Typography.Link>
              </>
            ) : null}
          </span>
        </div>
      </ConfigProvider>
      {isError ? (
        <Typography.Paragraph type="danger" data-testid="cc-caseload-error">
          {t('caseload.loadFailed')}
        </Typography.Paragraph>
      ) : null}
      <Table<CaseloadRow>
        className={styles.table}
        size="middle"
        rowKey="subject"
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        pagination={rows.length > 50 ? { pageSize: 50, showSizeChanger: false } : false}
        showSorterTooltip={false}
        scroll={{ x: 'max-content' }}
        data-testid="cc-caseload-table"
        onChange={(_p, _f, sorter) => {
          const s = (Array.isArray(sorter) ? sorter[0] : sorter) as SorterResult<CaseloadRow> | undefined;
          const key = (s?.columnKey as SortKey | undefined) ?? null;
          const order = (s?.order as SortOrder | undefined) ?? null;
          setView({ sort: key && order ? key : null, order: key && order ? order : null });
        }}
        onRow={(r) =>
          ({
          className: 'cc-caseload-row',
          tabIndex: 0,
          'data-href': clientHref(r.subject),
          'data-testid': `cc-caseload-row-${keyOf(r.subject)}`,
          onClick: (e: React.MouseEvent) => {
            if (interactive(e.target)) return;
            openRow(r.subject, e.metaKey || e.ctrlKey);
          },
          onAuxClick: (e: React.MouseEvent) => {
            if (e.button !== 1 || interactive(e.target)) return;
            e.preventDefault();
            openRow(r.subject, true);
          },
          onMouseDown: (e: React.MouseEvent) => {
            // a middle press would start the browser's autoscroll instead of the auxclick
            if (e.button === 1 && !interactive(e.target)) e.preventDefault();
          },
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && e.target === e.currentTarget) openRow(r.subject, e.metaKey || e.ctrlKey);
          },
          }) as React.HTMLAttributes<HTMLElement>
        }
        locale={{
          emptyText: (
            <Empty
              description={all.length === 0 ? t('caseload.empty') : t('caseload.noMatch')}
              data-testid="cc-caseload-empty"
            />
          ),
        }}
      />
      <Typography.Paragraph type="secondary" style={{ marginTop: 10, fontSize: 13 }}>
        <span data-testid="cc-caseload-footer">{t('caseload.footer')}</span>{' '}
        <span data-testid="cc-caseload-row-hint">{t('caseload.rowHint')}</span>
      </Typography.Paragraph>
    </Card>
  );
};
